import React, { useState, useEffect, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { Box, Divider, Stack, Typography, Alert, useTheme } from "@mui/material";
import getClient from "../../helpers/get-client";
import { describeAuthError } from "../../helpers/authentication-helpers";
import {
  completarLoginGoogle,
  cuerpoPostGoogle,
  esRequiereTerminos,
  faltaCasillaGoogle,
} from "../../helpers/google-login";

const client = getClient();

const isConfigured = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

// #1453 R7/R11 (reglas en helpers/google-login.js):
// - `requireTos` (registro): sin la casilla marcada NO se llama al servidor
//   (se avisa con `onTosMissing`).
// - `soloCuentaExistente` (login, sin casilla a la vista): el POST pide no
//   crear cuentas. Si el servidor contesta 428 `requiere_terminos` (cuenta
//   nueva), se llama `onRequiereTerminos(reintentar)`: el caller muestra la
//   casilla y `reintentar()` reenvía el MISMO id_token, exigiendo la casilla
//   antes del POST.
// - Con la casilla marcada el POST lleva `accept_tos: true` y el backend
//   registra la aceptación si la cuenta nace ahora.
// El botón de Google vive en un iframe y no se puede deshabilitar: el corte va
// antes del POST. `showDivider` = el "O" de arriba (cuando Google va DEBAJO de
// otro bloque).
//
// `bloqueadoPorTos` (#1453, 30-sep): el médico tocaba Google sin la casilla,
// Google abría y cerraba su ventana, y el único aviso quedaba al fondo del
// formulario (fuera de pantalla en el celular): "la página no funciona". Con
// la casilla sin marcar el botón se ve atenuado, queda `inert` y un botón
// transparente encima captura el toque y llama `onClickBloqueado` (el caller
// resalta la casilla), sin abrir Google. `onTosMissing(reintentar)` sigue
// como red por si el toque llega igual al iframe: `reintentar` reenvía el
// mismo id_token cuando la casilla ya esté marcada.
const GoogleSignInButton = ({
  onAfterLogin,
  requireTos = false,
  tosAccepted = false,
  onTosMissing,
  soloCuentaExistente = false,
  onRequiereTerminos,
  showDivider = true,
  bloqueadoPorTos = false,
  onClickBloqueado,
  // #1439: el acceso es siempre oscuro (vidrio) y el mockup pide el botón en
  // blanco como camino principal: "outline" es el blanco de Google.
  temaGoogle,
  // Llega una credencial NUEVA de Google (no un reintento): el caller descarta
  // lo pendiente de la anterior. Ver `enviarCredencial`.
  onNuevaCredencial,
}) => {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const muiTheme = useTheme();
  // El botón lo dibuja Google DENTRO DE UN IFRAME: no se puede tocar por CSS
  // desde acá (cross-origin), así que el estilo se pide por props. El azul de
  // Google (`filled_blue`) se peleaba con el teal de la app y venía con marco
  // claro alrededor; `filled_black` sobre el fondo oscuro y `outline` sobre el
  // claro son los dos que Google ofrece y que no meten un color de marca ajeno.
  // La forma sigue el vocabulario del resto de la app (pills 999).
  const modoOscuro = muiTheme.palette.mode === "dark";

  // #1401: el botón real de Google vive en un iframe cross-origin que
  // Playwright no puede apretar (design.md "Simulación de Google en los
  // tests"). Este hook deja disponible SOLO fuera de producción una forma de
  // ejercer el mismo POST /api/user/google/ + completarLoginGoogle que usa el
  // click real, para que el E2E de R20/R23/R27 pueda simular "el médico
  // completó el login con Google" sin el iframe. `NODE_ENV` es reemplazado
  // por webpack en build time: en el bundle de producción esta rama es
  // código muerto y terser la elimina (el revisor lo verifica con
  // `grep -c __smartdxTest` sobre el bundle servido).
  //
  // #1453: el hook pasa por `enviarCredencial`, la MISMA función del click
  // real, así que respeta la casilla de Términos (sin ella devuelve
  // `{ bloqueado: true }` y no hace el POST; ante una cuenta nueva desde el
  // login devuelve `{ requiereTerminos: true }`). Va por ref porque el effect
  // se registra una sola vez y la casilla cambia después.
  const tosRef = useRef({});
  tosRef.current = {
    requireTos,
    tosAccepted,
    onTosMissing,
    soloCuentaExistente,
    onRequiereTerminos,
    onNuevaCredencial,
  };

  // `crear` = reintento después del 428: ahí la casilla es obligatoria.
  const enviarCredencial = async (idToken, { crear = false } = {}) => {
    const tos = tosRef.current;
    // #1453 (revisor ronda 2, 02-oct): cualquier credencial nueva invalida lo
    // pendiente de la anterior ANTES de saber cómo contesta el servidor. Antes
    // solo lo borraba el 428 de la cuenta nueva: si B fallaba (500, corte de
    // red), marcar la casilla creaba A, la cuenta que el médico ya había
    // dejado. `crear` = reintento de la MISMA credencial: ahí no se borra.
    if (!crear && tos.onNuevaCredencial) tos.onNuevaCredencial(idToken);
    if (faltaCasillaGoogle({ requireTos: tos.requireTos, crear, tosAccepted: tos.tosAccepted })) {
      // `crear: true` en el reintento: vuelve a exigir la casilla antes del POST.
      if (tos.onTosMissing) tos.onTosMissing(() => procesarRef.current(idToken, { crear: true }));
      return { bloqueado: true };
    }
    const body = cuerpoPostGoogle({
      idToken,
      tosAccepted: tos.tosAccepted,
      soloCuentaExistente: tos.soloCuentaExistente,
    });
    let response;
    try {
      response = await client.post("/api/user/google/", body);
    } catch (err) {
      if (esRequiereTerminos(err)) {
        if (tos.onRequiereTerminos) {
          tos.onRequiereTerminos(() => procesarRef.current(idToken, { crear: true }));
        }
        return { requiereTerminos: true };
      }
      throw err;
    }
    return { target: completarLoginGoogle(response.data), data: response.data };
  };
  const enviarRef = useRef(enviarCredencial);
  enviarRef.current = enviarCredencial;

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return undefined;
    window.__smartdxTest = window.__smartdxTest || {};
    window.__smartdxTest.completarLoginGoogleDeTest = (idToken) => enviarRef.current(idToken);
    return () => {
      if (window.__smartdxTest) {
        delete window.__smartdxTest.completarLoginGoogleDeTest;
      }
    };
  }, []);

  // Un solo envío a la vez (#1453, 30-sep): al marcar la casilla el alta sigue
  // sola, y el médico por reflejo aprieta además "Crear mi cuenta y entrar".
  // Eran 2 POST con el mismo id_token (el revisor lo midió: el segundo volvía
  // `created:false` y lo sacaba del onboarding; en el back, dos create del mismo
  // correo sin lock pueden dar 500). Tras un envío OK queda tomado: se navega.
  const enCursoRef = useRef(false);

  // Click real (y `reintentar` tras el 428): envía y, si entró, navega.
  const procesarCredencial = async (idToken, opts = {}) => {
    setError("");
    const tos = tosRef.current;
    if (faltaCasillaGoogle({ requireTos: tos.requireTos, crear: opts.crear, tosAccepted: tos.tosAccepted })) {
      // #1453: sin la casilla no se llama al servidor (mismo corte que el hook).
      await enviarCredencial(idToken, opts);
      return;
    }
    if (enCursoRef.current) return;
    enCursoRef.current = true;
    let entro = false;
    setBusy(true);
    try {
      const resultado = await enviarCredencial(idToken, opts);
      // Cuenta nueva desde el login: el caller ya muestra la casilla.
      if (resultado.requiereTerminos || resultado.bloqueado) return;
      entro = true;
      const { target, data } = resultado;
      // #1401 (R20): `completarLoginGoogle` guarda el token (mismo camino que
      // el login por clave: arranca el reloj de inactividad) y devuelve el
      // destino — el onboarding /perfil/verificacion para una cuenta recién
      // creada (#1525 R7; antes /turno), /authentication (que resuelve con
      // destinoSegunCuenta) para una que ya existía. Antes de #1401 esto
      // mandaba SIEMPRE a /perfil/verificacion, incluso a una cuenta ya
      // aprobada con acceso completo.
      if (onAfterLogin) {
        onAfterLogin(data);
      }
      window.location.href = target;
    } catch (err) {
      // Tres fuentes, en orden. `describeAuthError` va antes que `err.message`
      // porque traduce los errores de red a algo accionable ("puede ser el
      // firewall de tu institución") en vez de mostrarle al médico un
      // "Network Error" o un "timeout of 15000ms exceeded" en inglés.
      // `err.message` queda para los errores NUESTROS (ej. el navegador no deja
      // guardar la sesión), que sin esto caían en el genérico y hacían que el
      // médico en Safari privado culpara a Google y reintentara para siempre.
      const esDeRed = err?.request && !err?.response;
      const detail =
        err?.response?.data?.detail ||
        (esDeRed ? describeAuthError(err).message : null) ||
        (!err?.response && err?.message) ||
        "No pudimos iniciar sesión con Google.";
      setError(detail);
    } finally {
      if (!entro) enCursoRef.current = false;
      setBusy(false);
    }
  };
  const procesarRef = useRef(procesarCredencial);
  procesarRef.current = procesarCredencial;

  if (!isConfigured) {
    return null;
  }

  const handleCredential = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      setError("Google no devolvió credenciales válidas.");
      return;
    }
    await procesarCredencial(idToken);
  };

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      {showDivider && (
      <Divider
        sx={{
          color: "rgba(var(--fg-rgb),0.5)",
          fontSize: "0.75rem",
          my: 1,
          "&::before, &::after": { borderColor: "rgba(var(--fg-rgb),0.15)" },
        }}
      >
        O
      </Divider>
      )}
      {/* minHeight 44: el botón transparente es inset:0 de esta caja, y el de
          Google mide 40; así el área táctil llega a 44 (revisor #1453). */}
      <Box sx={{ position: "relative", minHeight: 44, display: "flex", alignItems: "center" }}>
        <Box
          data-bloqueado-por-tos={bloqueadoPorTos ? "true" : "false"}
          {...(bloqueadoPorTos ? { inert: "", "aria-hidden": "true" } : {})}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            opacity: bloqueadoPorTos ? 0.45 : 1,
            transition: "opacity 160ms ease",
            // El iframe de Google trae su propio fondo y esquinas rectas: sin
            // esto asomaba un marco claro alrededor del botón (lo que se veía
            // como un borde blanco grueso sobre el fondo oscuro del login).
            "& > div": {
              borderRadius: "999px",
              overflow: "hidden",
              lineHeight: 0,
            },
            "& iframe": { colorScheme: "normal" },
          }}
        >
          <GoogleLogin
            onSuccess={handleCredential}
            onError={() => setError("Google rechazó la autenticación.")}
            theme={temaGoogle || (modoOscuro ? "filled_black" : "outline")}
            text="continue_with"
            shape="pill"
            size="large"
            width="320"
          />
        </Box>
        {bloqueadoPorTos && (
          <Box
            component="button"
            type="button"
            data-testid="google-bloqueado-por-tos"
            aria-label="Continuar con Google: primero acepta los términos y condiciones"
            onClick={onClickBloqueado}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              border: 0,
              p: 0,
              m: 0,
              background: "transparent",
              cursor: "pointer",
              borderRadius: "999px",
              "&:focus-visible": { outline: "2px solid var(--color-primary)", outlineOffset: 2 },
            }}
          />
        )}
      </Box>
      {busy && (
        <Typography
          variant="caption"
          sx={{ textAlign: "center", color: "rgba(var(--fg-rgb),0.6)" }}
        >
          Iniciando sesión...
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};

export default GoogleSignInButton;
